/**
 * Amplifier profiles -- group 08 (valve houses, plus one valve/switching
 * hybrid).
 *
 * Seven of these eight are thermionic designs, so the differentiation cannot
 * come from the word "tube": it has to come from the numbers that actually
 * change the DSP. The axes used here are output-device family (EL84 through
 * 845), transformer corner frequencies, source impedance, feedback depth and
 * supply stiffness. A 32 W EL84 integrated with a 3.2 ohm source impedance and
 * a 22 Hz transformer corner and a 425 W 6550 monoblock with a 0.67 ohm source
 * impedance and a 12 Hz corner produce completely different results on the same
 * loudspeaker, which is the point.
 */

import type { AmpProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeAmp, usd } from '../kit';

export const AMPS_08: AmpProfile[] = [
  makeAmp({
    mfr: 'Fezz Audio',
    model: 'Titania',
    price: usd(2600, 'APPROXIMATE', 2023, 'Approximate US retail; Fezz sells through regional distributors and pricing varies noticeably by market.'),
    year: 2016,
    country: 'Poland',
    topology: 'tube-push-pull',
    form: 'integrated',
    devices: '4 x KT88 in ultralinear push-pull, solid-state rectification, in-house wound output transformers',
    power: { watts8: 40, watts4: 38, confidence: 'ESTIMATED' },
    gainDb: 34,
    thdPercent: 0.5,
    snrDb: 88,
    bandwidth: [15, 40000],
    outputTransformer: true,
    transformerLfHz: 18,
    transformerHfHz: 34000,
    dampingFactor: 4,
    feedbackDb: 10,
    crosstalkDb: -62,
    harmonicShape: HARMONIC_SHAPES.pushPullTube,
    nonlinear: { kind: 'pentode', amount: 0.58, asymmetry: 0.28, bias: 0.22, headroomDb: 10, oversample: 4 },
    dynamics: { sagDb: 1.0, sagTimeMs: 170, recoveryMs: 400, slewRateVus: 18, headroomDb: 1.2 },
    clipping: { kind: 'soft', kneeDb: 7, stickiness: 0.32 },
    confidence: 'ESTIMATED',
    accent: '#b58900',
    tags: ['kt88', 'ultralinear', 'solid-state-rectified', 'entry-valve'],
    notes:
      'Ultralinear KT88 push-pull with moderate global feedback, which is the middle of the road for this group in every dimension. The push-pull pair cancels most of the second harmonic so the residue is third-order dominant, and the 2 ohm source impedance lets roughly a decibel of the loudspeaker impedance curve print itself onto the frequency response. Solid-state rectification keeps the supply comparatively stiff, so the modelled sag is about half that of the valve-rectified single-ended designs here and recovery is faster.',
    provenanceNote:
      'Output-device complement, ultralinear operation and the integrated form are from Fezz Audio published material. Output power, THD, signal-to-noise, bandwidth and damping factor are ESTIMATED from KT88 ultralinear norms: Fezz publishes only summary specifications and does not publish a THD figure at rated power.',
  }),

  makeAmp({
    mfr: 'Icon Audio',
    model: 'HP8 MkII',
    price: usd(1900, 'APPROXIMATE', 2020, 'Approximate US equivalent of the UK retail price; Icon Audio prices in GBP and sells largely direct.'),
    year: 2013,
    country: 'United Kingdom',
    topology: 'transformer-coupled',
    form: 'headphone',
    devices: 'Valve gain stage into a wide-bandwidth output transformer with selectable headphone impedance taps',
    power: { mw32: 300, mw300: 1200, mw600: 800, confidence: 'ESTIMATED' },
    gainDb: 18,
    thdPercent: 0.6,
    snrDb: 86,
    bandwidth: [10, 45000],
    outputTransformer: true,
    transformerLfHz: 14,
    transformerHfHz: 40000,
    outputImpedanceOhms: 8,
    feedbackDb: 6,
    crosstalkDb: -60,
    harmonicShape: HARMONIC_SHAPES.transformer,
    nonlinear: { kind: 'transformer-core', amount: 0.55, coreKnee: 0.48, hysteresis: 0.24, headroomDb: 10, oversample: 4 },
    dynamics: { sagDb: 0.8, sagTimeMs: 160, recoveryMs: 380, slewRateVus: 16, headroomDb: 2.4 },
    clipping: { kind: 'soft', kneeDb: 7, stickiness: 0.3 },
    confidence: 'ESTIMATED',
    accent: '#cb4b16',
    tags: ['valve-headphone', 'output-transformer', 'impedance-taps'],
    notes:
      'Transformer coupling is what makes this different from an OTL valve headphone amplifier. The load is reflected back to the valve through the square of the turns ratio, so a 32 ohm planar and a 600 ohm dynamic present the valve with plate loads that differ by a factor of nearly twenty, and the tap the user selects changes bandwidth and distortion as much as it changes level. Because the transformer steps the impedance down, the source impedance is around 8 ohms rather than the hundreds of ohms an OTL presents, so a dynamic driver is actually damped. The dominant nonlinearity at high level is core hysteresis at low frequency, not valve curvature.',
    provenanceNote:
      'Valve topology, output transformer coupling and the multiple headphone impedance taps are from Icon Audio published material. Output power into 32/300/600 ohms, THD, signal-to-noise, bandwidth and source impedance are all ESTIMATED: Icon Audio does not publish milliwatt figures per load or a THD specification for this model.',
  }),

  makeAmp({
    mfr: 'PrimaLuna',
    model: 'EVO 400 Integrated',
    price: usd(5499, 'APPROXIMATE', 2023, 'Approximate US retail; PrimaLuna revises dealer pricing periodically.'),
    year: 2019,
    country: 'Netherlands',
    topology: 'tube-push-pull',
    form: 'integrated',
    devices: '8 x KT150 push-pull, Adaptive AutoBias, switchable ultralinear and triode operation',
    power: { watts8: 70, watts4: 70, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 36,
    thdPercent: 0.4,
    snrDb: 90,
    bandwidth: [12, 55000],
    outputTransformer: true,
    transformerLfHz: 16,
    transformerHfHz: 48000,
    dampingFactor: 6,
    feedbackDb: 12,
    crosstalkDb: -70,
    harmonicShape: HARMONIC_SHAPES.pushPullTube,
    nonlinear: { kind: 'pentode', amount: 0.5, asymmetry: 0.18, bias: 0.2, headroomDb: 11, oversample: 4 },
    dynamics: { sagDb: 0.7, sagTimeMs: 150, recoveryMs: 340, slewRateVus: 20, headroomDb: 1.5 },
    clipping: { kind: 'soft', kneeDb: 6.5, stickiness: 0.28 },
    confidence: 'ESTIMATED',
    accent: '#268bd2',
    tags: ['kt150', 'auto-bias', 'triode-switchable', 'wide-bandwidth'],
    notes:
      'Adaptive AutoBias continuously re-references each output valve rather than setting bias once at installation, and the modelling consequence is specific: push-pull second-harmonic cancellation depends on the two halves being matched, so an amplifier that holds bias holds its H2 null instead of letting it drift upward as the valves age. Eight KT150s into a large transformer give it the stiffest low end of the integrated amplifiers here, with a 16 Hz transformer corner and a damping factor of 6. Triode mode roughly halves the power, drops the feedback available and shifts the residue back toward the second harmonic.',
    provenanceNote:
      'Output power in ultralinear and triode modes, valve complement and Adaptive AutoBias are from PrimaLuna published specifications. THD, signal-to-noise, damping factor and transformer corner frequencies are ESTIMATED; PrimaLuna publishes power and bandwidth but not a THD figure at rated output.',
  }),

  makeAmp({
    mfr: 'Rogue Audio',
    model: 'Sphinx V3',
    price: usd(1795, 'MSRP', 2021),
    year: 2020,
    country: 'United States',
    topology: 'hybrid',
    form: 'integrated',
    devices: '12AU7 valve input and driver stage into a switching output stage, with an onboard MM phono input',
    power: { watts8: 100, watts4: 200, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 37,
    thdPercent: 0.1,
    snrDb: 95,
    bandwidth: [5, 45000],
    outputTransformer: false,
    dampingFactor: 120,
    feedbackDb: 24,
    crosstalkDb: -88,
    harmonicShape: HARMONIC_SHAPES.classD,
    nonlinear: { kind: 'class-d-pwm', amount: 0.35, carrierHz: 500000, asymmetry: 0.3, bias: 0.35, headroomDb: 9, oversample: 4 },
    dynamics: { sagDb: 0.2, sagTimeMs: 40, recoveryMs: 90, slewRateVus: 28, headroomDb: 0.9 },
    clipping: { kind: 'hard', kneeDb: 0.8, stickiness: 0.08 },
    confidence: 'ESTIMATED',
    accent: '#6c71c4',
    tags: ['valve-input', 'switching-output', 'high-damping', 'phono-stage'],
    notes:
      'The only non-thermionic output stage in this group and the reason it is here: the valve front end contributes an asymmetric, second-harmonic-weighted term at small signal, while the switching output contributes an odd-order, high-frequency-weighted residue and a source impedance two orders of magnitude below every other entry. That combination is modelled as a PWM nonlinearity carrying a deliberate asymmetry from the input stage. With a damping factor of 120 the loudspeaker impedance curve is essentially flattened out of the response, so on a difficult load this amplifier and the 845 monoblocks below will not even produce the same frequency response.',
    provenanceNote:
      'Output power into 8 and 4 ohms, the valve input stage and the phono input are from Rogue Audio published specifications. THD, signal-to-noise, damping factor and bandwidth are ESTIMATED from class norms for a valve-input switching integrated; Rogue publishes power but not a full distortion-versus-power characteristic.',
  }),

  makeAmp({
    mfr: 'Cary Audio Design',
    model: 'CAD-805RS',
    price: usd(17000, 'APPROXIMATE', 2019, 'Approximate US retail per pair. Long-running model with several revisions, so the figure brackets the RS generation rather than quoting one price list.'),
    year: 2011,
    country: 'United States',
    topology: 'tube-set',
    form: 'monoblock',
    devices: '845 directly-heated triode single-ended output, valve rectification, choke-loaded supply',
    power: { watts8: 50, watts4: 30, confidence: 'ESTIMATED' },
    gainDb: 30,
    thdPercent: 1.5,
    snrDb: 82,
    bandwidth: [16, 28000],
    outputTransformer: true,
    transformerLfHz: 20,
    transformerHfHz: 25000,
    outputImpedanceOhms: 2.8,
    feedbackDb: 3,
    crosstalkDb: -95,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.9, asymmetry: 0.82, bias: 0.88, headroomDb: 7, oversample: 8 },
    dynamics: { sagDb: 2.2, sagTimeMs: 260, recoveryMs: 640, slewRateVus: 13, headroomDb: 0.8 },
    clipping: { kind: 'soft', kneeDb: 9, stickiness: 0.4 },
    confidence: 'ESTIMATED',
    accent: '#dc9656',
    tags: ['845', 'single-ended', 'directly-heated', 'valve-rectified'],
    notes:
      'A single-ended 845 running at a very high plate voltage: with only one output device there is no even-order cancellation, so the second harmonic sits within roughly 20 dB of the fundamental at full output and climbs about 1 dB per dB of drive, the steepest distortion-versus-level slope in this group. The 2.8 ohm source impedance is high enough that the loudspeaker impedance curve appears directly in the frequency response, which is why this profile only makes sense paired with a benign, high-sensitivity load. Separate chassis per channel removes shared-supply crosstalk and, more importantly, removes the cross-channel supply modulation that makes a single-chassis valve amplifier smear during loud passages.',
    provenanceNote:
      'The 845 single-ended topology, valve rectification and monoblock construction are from Cary Audio Design published material. Output power, THD, signal-to-noise, bandwidth and source impedance are ESTIMATED from 845 single-ended norms; Cary does not publish a THD figure at rated power for this model, and the feedback control fitted to the amplifier means source impedance is in any case user-adjustable.',
  }),

  makeAmp({
    mfr: 'conrad-johnson',
    model: 'ET3 SE',
    price: usd(5000, 'APPROXIMATE', 2018, 'Approximate US retail for the SE version with the upgraded capacitor set.'),
    year: 2013,
    country: 'United States',
    topology: 'tube-set',
    form: 'preamp',
    devices: 'Single triode voltage-gain stage per channel, zero global feedback, polystyrene and film-bypassed supply',
    power: { outputVrms: 20, confidence: 'ESTIMATED' },
    gainDb: 25,
    thdPercent: 0.15,
    snrDb: 96,
    bandwidth: [2, 100000],
    outputTransformer: false,
    outputImpedanceOhms: 300,
    feedbackDb: 0,
    crosstalkDb: -85,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.4, asymmetry: 0.6, bias: 0.7, headroomDb: 18, oversample: 8 },
    dynamics: { sagDb: 0.3, sagTimeMs: 120, recoveryMs: 300, thermalDb: 0.05, slewRateVus: 30, headroomDb: 12 },
    clipping: { kind: 'soft', kneeDb: 8, stickiness: 0.15, onsetDbFS: -0.5 },
    confidence: 'ESTIMATED',
    accent: '#93a1a1',
    tags: ['line-stage', 'zero-feedback', 'single-triode', 'no-output-transformer'],
    notes:
      'The only line stage in this group, and it behaves fundamentally differently from the power amplifiers around it: with a 20 Vrms swing available it never approaches clipping in normal use, so what it contributes is a fixed small-signal second-harmonic term plus its own noise floor, not a level-dependent overload characteristic. There is no output transformer, so no core saturation and no low-frequency corner worth modelling, but the roughly 300 ohm source impedance forms a low-pass filter with the input capacitance of the following amplifier and a long interconnect, which is the real load sensitivity here. Zero global feedback means the harmonic residue decays quickly with order rather than being pushed up into high orders.',
    provenanceNote:
      'Zero-feedback single-triode line-stage topology, gain and the wide bandwidth are from conrad-johnson published material. Output impedance, maximum output voltage, THD and signal-to-noise are ESTIMATED. conrad-johnson does not publish a THD figure for its line stages, which is common practice among low-feedback valve preamplifier makers.',
  }),

  makeAmp({
    mfr: 'VTL',
    model: 'MB-450 Series III Signature',
    price: usd(30000, 'APPROXIMATE', 2020, 'Approximate US retail per pair for the Series III Signature generation.'),
    year: 2013,
    country: 'United States',
    topology: 'tube-push-pull',
    form: 'monoblock',
    devices: '8 x 6550C push-pull, switchable tetrode and triode operation, auto-bias with fault sensing',
    power: { watts8: 425, watts4: 425, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 28,
    thdPercent: 0.6,
    snrDb: 92,
    bandwidth: [10, 60000],
    outputTransformer: true,
    transformerLfHz: 12,
    transformerHfHz: 55000,
    dampingFactor: 12,
    feedbackDb: 14,
    crosstalkDb: -100,
    harmonicShape: HARMONIC_SHAPES.pushPullTube,
    nonlinear: { kind: 'pentode', amount: 0.45, asymmetry: 0.14, bias: 0.16, headroomDb: 13, oversample: 4 },
    dynamics: { sagDb: 0.6, sagTimeMs: 150, recoveryMs: 350, slewRateVus: 24, headroomDb: 1.6 },
    clipping: { kind: 'soft', kneeDb: 5.5, stickiness: 0.22 },
    confidence: 'ESTIMATED',
    accent: '#a37acc',
    tags: ['6550', 'high-power', 'tetrode-triode-switchable', 'auto-bias'],
    notes:
      'Eight output valves per channel into a very large transformer, and the modelling consequence of that much reserve is subtle but real: at normal listening levels the amplifier is operating perhaps 25 dB below rated power, so the push-pull third-harmonic term sits far lower than a 30 W design driven to the same acoustic output would produce, and the clipping model is essentially never invoked. The low-frequency limit is set by the transformer core rather than by supply sag, hence the 12 Hz corner. A damping factor of 12 is high for a valve amplifier but still two orders below the switching design here, so a decibel-scale impedance interaction remains.',
    provenanceNote:
      'Rated output in tetrode and triode modes, valve complement and the auto-bias fault-sensing system are from VTL published specifications. THD, signal-to-noise, damping factor and transformer corner frequencies are ESTIMATED; VTL publishes power and bandwidth but not a full distortion-versus-power curve.',
  }),

  makeAmp({
    mfr: 'Manley Laboratories',
    model: 'Stingray II',
    price: usd(5500, 'APPROXIMATE', 2020, 'Approximate US retail; recalled rather than transcribed from a current price list.'),
    year: 2013,
    country: 'United States',
    topology: 'tube-push-pull',
    form: 'integrated',
    devices: '8 x EL84 push-pull, 12AT7 input and driver, switchable ultralinear and triode operation',
    power: { watts8: 32, watts4: 25, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 32,
    thdPercent: 0.8,
    snrDb: 85,
    bandwidth: [18, 30000],
    outputTransformer: true,
    transformerLfHz: 22,
    transformerHfHz: 27000,
    dampingFactor: 2.5,
    feedbackDb: 6,
    crosstalkDb: -58,
    harmonicShape: HARMONIC_SHAPES.pushPullTube,
    nonlinear: { kind: 'pentode', amount: 0.66, asymmetry: 0.34, bias: 0.3, headroomDb: 9, oversample: 4 },
    dynamics: { sagDb: 1.8, sagTimeMs: 200, recoveryMs: 520, slewRateVus: 16, headroomDb: 1.0 },
    clipping: { kind: 'soft', kneeDb: 7.5, stickiness: 0.36 },
    confidence: 'ESTIMATED',
    accent: '#d33682',
    tags: ['el84', 'small-signal-valves', 'triode-switchable', 'low-damping'],
    notes:
      'EL84s are small-signal output valves and the transformers that suit them are correspondingly small, which puts the low-frequency corner at 22 Hz -- the highest here -- and makes the core the first thing to run out of headroom. Low-frequency distortion therefore rises well before the valves themselves clip, so the modelled transformer-core term matters more on this amplifier than on the 6550 and KT150 designs. A damping factor of 2.5 gives the strongest loudspeaker impedance interaction of any push-pull entry in this group, roughly 1.5 dB of response deviation on a typical two-way, and the modest supply produces almost twice the sag of the PrimaLuna.',
    provenanceNote:
      'Output power in ultralinear and triode modes and the valve complement are from Manley Laboratories published specifications. THD, signal-to-noise, damping factor, bandwidth and transformer corner frequencies are ESTIMATED from EL84 push-pull norms; Manley publishes power and frequency response but not a THD figure at rated output.',
  }),
];
